import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Link,
  CircularProgress,
} from "@mui/material";
import {
  signInWithEmailAndPassword,
  sendEmailVerification,
  setPersistence,
  browserLocalPersistence,
  getAuth,
} from "firebase/auth";
import { auth } from "../../firebase"; // central auth import
import { useAuth } from "../../context/AuthContext";
import { useSnackbarAlert } from "../../hooks/useSnackbarAlert";
import SnackbarAlert from "../../components/SnackbarAlert";
import SocialSignInButtons from "./SocialSignInButtons";
import api from "../../services/api";

function LoginPage() {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showResendVerification, setShowResendVerification] = useState(false);

  const { handleLogin } = useAuth();
  const snackbar = useSnackbarAlert();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.returnTo || "/";

  useEffect(() => {
    document.title = "Log in";
  }, []);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setError("");
    setSuccessMessage("");
    setShowResendVerification(false);

    if (!formData.email || !formData.password) {
      setError("Email and Password are required.");
      return;
    }

    setLoading(true);

    try {
      // Step 1: Ensure session is persisted
      await setPersistence(auth, browserLocalPersistence);

      // Step 2: Sign in
      const userCredential = await signInWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      const firebaseUser = userCredential.user;

      // Step 3: Reload user state
      await firebaseUser.reload();
      const refreshedUser = getAuth().currentUser;

      // Step 4: Check email verification
      if (!refreshedUser.emailVerified) {
        setShowResendVerification(true);
        setError(
          "Your email is not verified. Please verify your email to log in."
        );
        return;
      }

      // Step 5: Get fresh ID token
      const firebaseToken = await refreshedUser.getIdToken(true);
      const decoded = JSON.parse(atob(firebaseToken.split(".")[1]));
      console.log("Decoded ID token:", decoded);

      // Step 6: Call backend login (✅ note the fix: move token to headers correctly)
      const authResponse = await api.post(
        `/auth/login`,
        {}, // body must be an object or Axios will omit it
        {
          headers: {
            Authorization: `Bearer ${firebaseToken}`,
          },
        }
      );

      // Step 7: Store backend tokens if needed (optional)
      await handleLogin(
        authResponse.data.accessToken,
        authResponse.data.refreshToken
      );

      // Step 8: Fetch user profile
      const userResponse = await api.get("/users/me");
      const userData = userResponse.data;

      if (userData.firstVisitAfterCrash === true) {
        navigate("/update-info");
      } else {
        navigate(returnTo);
      }
    } catch (err) {
      console.error("Login error:", err);

      let message = "Login failed. Please try again later.";

      if (err.code === "auth/user-not-found") {
        message = "No account found with this email.";
      } else if (err.code === "auth/wrong-password") {
        message = "Invalid email or password.";
      } else if (err.code === "auth/too-many-requests") {
        message = "Too many failed attempts. Please wait and try again.";
      }

      // Server-side errors
      if (err.response?.status === 404) {
        message = "User not found in our system.";
      } else if (err.response?.data?.message) {
        message = err.response.data.message;
      } else if (err.response?.data?.error) {
        message = err.response.data.error;
      }

      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      setError("");
      setSuccessMessage("");

      const currentUser = auth.currentUser;
      if (!currentUser) {
        setError("No user is signed in to resend verification.");
        return;
      }

      await sendEmailVerification(currentUser);
      setSuccessMessage("Verification email resent. Check your inbox.");
    } catch (err) {
      console.error("Resend verification failed:", err.message);
      if (err.code === "auth/too-many-requests") {
        setError("Too many requests. Please wait and try again later.");
      } else {
        setError("Could not resend verification. Try again later.");
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  return (
    <Container maxWidth="sm">
      <Box
        mt={5}
        mb={5}
        p={4}
        boxShadow={3}
        borderRadius={2}
        onKeyDown={handleKeyDown}>
        <Typography variant="h4" align="center" gutterBottom>
          Login
        </Typography>

        <TextField
          label="Email"
          name="email"
          fullWidth
          margin="normal"
          value={formData.email}
          onChange={handleInputChange}
        />

        <TextField
          label="Password"
          name="password"
          type="password"
          fullWidth
          margin="normal"
          value={formData.password}
          onChange={handleInputChange}
          autoComplete="new-password"
          sx={{
            input: { WebkitTextSecurity: "disc" },
          }}
        />

        <Box textAlign="right">
          <Link
            href="/forgot-password"
            variant="body2"
            sx={{ color: "primary.main", cursor: "pointer" }}>
            Forgot password?
          </Link>
        </Box>

        <Box mt={3}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmit}
            disabled={loading}>
            {loading ? (
              <CircularProgress size={24} sx={{ color: "#fff" }} />
            ) : (
              "Login"
            )}
          </Button>
        </Box>

        <Box mt={3}>
          <SocialSignInButtons />
        </Box>

        {showResendVerification && (
          <Box mt={3} textAlign="center">
            <Typography variant="body2" mb={1}>
              Didn’t receive the verification email?
            </Typography>
            <Button
              variant="text"
              onClick={handleResendVerification}
              disabled={loading}>
              Resend Verification Email
            </Button>
          </Box>
        )}

        <Box mt={2} textAlign="center">
          <Typography variant="body2">
            Don’t have an account?{" "}
            <Link href="/signup" variant="body2" sx={{ color: "primary.main" }}>
              Sign up
            </Link>
          </Typography>
        </Box>
      </Box>

      <SnackbarAlert
        open={!!successMessage}
        onClose={() => setSuccessMessage("")}
        message={successMessage}
        severity="success"
        bgColor="#4CAF50"
        iconColor="#fff"
      />
      <SnackbarAlert
        open={!!error}
        onClose={() => setError("")}
        message={error}
        severity="error"
        bgColor="#DA2B1D"
        iconColor="#fff"
      />
    </Container>
  );
}

export default LoginPage;
